import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Plus, Trash2, CheckCircle2, MinusCircle, GraduationCap, Pencil, UserPlus } from 'lucide-react';

const emptyEmpForm = { name: '', email: '', department_id: '' };

export default function Employees() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [personalEmails, setPersonalEmails] = useState(new Set());
  const [formData, setFormData] = useState(emptyEmpForm);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadEmployees();
    loadDepartments();
    loadPersonal();
  }, []);

  const loadEmployees = async () => {
    try {
      const response = await api.getEmployees();
      setEmployees(response.data);
    } catch (error) {
      console.error('Error loading employees:', error);
      toast.error('Error al cargar empleados');
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await api.getDepartments();
      setDepartments(response.data);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const loadPersonal = async () => {
    try {
      const response = await api.getPersonal();
      const emails = new Set(
        (response.data || [])
          .map((u) => (u.email || '').trim().toLowerCase())
          .filter(Boolean)
      );
      setPersonalEmails(emails);
    } catch (error) {
      // Sin token de admin u otro problema: simplemente no marcamos accesos
      console.warn('No se pudo cargar el personal para marcar accesos:', error);
    }
  };

  const hasAcademyAccess = (email) => {
    if (!email) return false;
    return personalEmails.has(email.trim().toLowerCase());
  };

  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept ? dept.name : 'N/A';
  };

  const handleNewClick = () => {
    setFormData(emptyEmpForm);
    setEditingId(null);
    setOpen(true);
  };

  const handleEditClick = (emp) => {
    setFormData({
      name: emp.name || '',
      email: emp.email || '',
      department_id: emp.department_id || '',
    });
    setEditingId(emp.id);
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.department_id) {
      toast.error('Complete todos los campos');
      return;
    }
    setLoading(true);
    try {
      if (editingId) {
        await api.updateEmployee(editingId, formData);
        toast.success('Empleado actualizado');
      } else {
        await api.createEmployee(formData);
        toast.success('Empleado creado');
      }
      setFormData(emptyEmpForm);
      setEditingId(null);
      setOpen(false);
      loadEmployees();
    } catch (error) {
      console.error('Error:', error);
      toast.error(editingId ? 'Error al actualizar empleado' : 'Error al crear empleado');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (emp) => {
    setDeleteConfirm(emp);
  };

  const confirmDelete = async () => {
    const emp = deleteConfirm;
    setDeleteConfirm(null);
    if (!emp) return;
    try {
      await api.deleteEmployee(emp.id);
      toast.success('Empleado eliminado');
      loadEmployees();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar empleado');
    }
  };

  const handleGrantAcademy = (emp) => {
    // Navega a /admin/personal y pre-rellena el formulario con los datos del empleado
    navigate('/admin/personal', { state: { prefillEmployee: emp } });
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl" data-testid="employees-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-tight">Empleados</h1>
            <p className="text-gray-600 mt-1">Gestione el personal de la empresa</p>
          </div>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setFormData(emptyEmpForm); } }}>
            <Button data-testid="add-employee-button" onClick={handleNewClick} className="bg-[#DC143C] hover:bg-[#B91C1C] text-white font-medium uppercase tracking-wide flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Agregar
            </Button>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold uppercase tracking-tight">{editingId ? 'Editar Empleado' : 'Nuevo Empleado'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="emp_name" className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Nombre Completo</Label>
                  <Input
                    id="emp_name"
                    data-testid="employee-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Juan Pérez"
                    className="h-11"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="emp_email" className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Correo Electrónico</Label>
                  <Input
                    id="emp_email"
                    data-testid="employee-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Ej: juan@proesa.com"
                    className="h-11"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="emp_dept" className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Departamento</Label>
                  <Select value={formData.department_id} onValueChange={(value) => setFormData({ ...formData, department_id: value })}>
                    <SelectTrigger id="emp_dept" data-testid="employee-department-select" className="h-11">
                      <SelectValue placeholder="Seleccione un departamento" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map(dept => (
                        <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button type="submit" data-testid="save-employee-button" disabled={loading} className="bg-[#DC143C] hover:bg-[#B91C1C] text-white">
                    {loading ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {employees.length > 0 && (
            <div className="px-6 py-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 flex flex-wrap items-center gap-4 text-sm" data-testid="employees-access-summary">
              <span className="flex items-center gap-1.5 text-gray-600">
                <GraduationCap className="h-4 w-4 text-[#DC143C]" />
                <strong className="text-gray-900">Acceso Academia:</strong>
              </span>
              <span className="text-green-700">
                <strong data-testid="employees-with-access-count">
                  {employees.filter((e) => hasAcademyAccess(e.email)).length}
                </strong> con cuenta
              </span>
              <span className="text-gray-500">
                <strong data-testid="employees-without-access-count">
                  {employees.filter((e) => !hasAcademyAccess(e.email)).length}
                </strong> sin cuenta
              </span>
              <span className="text-gray-400">·</span>
              <span className="text-gray-500">Total: <strong>{employees.length}</strong></span>
            </div>
          )}
          {employees.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <p>No hay empleados registrados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Departamento</th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acceso Academia</th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {employees.map((emp) => {
                    const hasAccess = hasAcademyAccess(emp.email);
                    return (
                      <tr key={emp.id} className="hover:bg-gray-50 transition-colors" data-testid="employee-row">
                        <td className="px-6 py-4 text-sm text-gray-900 font-medium">{emp.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{emp.email}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{getDepartmentName(emp.department_id)}</td>
                        <td className="px-6 py-4 text-center" data-testid="academy-access-cell">
                          {hasAccess ? (
                            <span
                              data-testid="academy-access-yes"
                              title="Este empleado ya tiene cuenta de Personal y acceso a la Academia"
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-medium"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              Sí
                            </span>
                          ) : (
                            <span
                              data-testid="academy-access-no"
                              title="Este empleado aún no tiene cuenta de Personal — créala en la sección Personal"
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 border border-gray-200 text-gray-500 text-xs font-medium"
                            >
                              <MinusCircle className="h-3.5 w-3.5" />
                              No
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right whitespace-nowrap">
                          {!hasAccess && (
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid="grant-academy-access-button"
                              onClick={() => handleGrantAcademy(emp)}
                              title="Dar acceso a la Academia (crear cuenta de Personal)"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              <UserPlus className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid="edit-employee-button"
                            onClick={() => handleEditClick(emp)}
                            title="Editar empleado"
                            className="text-gray-700 hover:bg-gray-100"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid="delete-employee-button"
                            onClick={() => handleDelete(emp)}
                            title="Eliminar empleado"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* AlertDialog: Confirmar eliminación */}
        <AlertDialog open={!!deleteConfirm} onOpenChange={(v) => !v && setDeleteConfirm(null)}>
          <AlertDialogContent data-testid="delete-employee-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-600" />
                Eliminar empleado
              </AlertDialogTitle>
              <AlertDialogDescription>
                {deleteConfirm && (
                  <>¿Eliminar a <strong>{deleteConfirm.name}</strong>? Esta acción no se puede deshacer.</>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-employee-cancel">Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} data-testid="delete-employee-ok" className="bg-red-600 hover:bg-red-700">
                Sí, eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
